import { Input } from "reactstrap";
import { AsyncPaginatedSelect } from "components/common/AsyncPaginatedSelect";
import { getClientOptions } from "features/Clients/services/ClientService";
import {
  AmcContractStatus,
  amcStatusMap,
} from "features/Amc/constants/amc-contract.constants";
import type { AmcListParams } from "features/Amc/services/AmcService";
import type { SelectOption } from "types/api.types";

export type AmcListFiltersState = {
  status: AmcContractStatus | "";
  clientId: number | "";
};

export function emptyAmcListFilters(): AmcListFiltersState {
  return { status: "", clientId: "" };
}

const AMC_STATUS_ORDER = [
  AmcContractStatus.DRAFT,
  AmcContractStatus.READY_FOR_ACTIVATION,
  AmcContractStatus.ACTIVE,
  AmcContractStatus.COMPLETED,
  AmcContractStatus.CANCELLED,
] as const;

export function amcListFiltersToListParams(
  f: AmcListFiltersState,
): Partial<AmcListParams> {
  return {
    ...(f.status ? { status: f.status } : {}),
    ...(f.clientId !== "" && Number.isFinite(f.clientId) && f.clientId > 0
      ? { clientId: f.clientId }
      : {}),
  };
}

export function countAmcListFiltersActive(f: AmcListFiltersState): number {
  let n = 0;
  if (f.status) n += 1;
  if (f.clientId !== "") n += 1;
  return n;
}

function userSelectOnChange(
  opt: SelectOption | SelectOption[] | null,
): number | "" {
  const val = Array.isArray(opt) ? (opt[0] ?? null) : opt;
  const v = val?.value;
  return typeof v === "number" && Number.isFinite(v) && v > 0 ? v : "";
}

export type AmcListFiltersProps = {
  filters: AmcListFiltersState;
  onChange: (next: AmcListFiltersState) => void;
};

export default function AmcListFilters({
  filters,
  onChange,
}: AmcListFiltersProps) {
  const patch = (partial: Partial<AmcListFiltersState>) =>
    onChange({ ...filters, ...partial });

  return (
    <div className="d-flex align-items-center gap-2 flex-wrap">
      <Input
        type="select"
        className="form-select"
        style={{ width: 150 }}
        value={filters.status}
        onChange={(e) =>
          patch({ status: e.target.value as AmcContractStatus | "" })
        }
      >
        <option value="">All statuses</option>
        {AMC_STATUS_ORDER.map((s) => (
          <option key={s} value={s}>
            {amcStatusMap[s]?.label ?? s}
          </option>
        ))}
      </Input>
      <div style={{ width: 200 }}>
        <AsyncPaginatedSelect
          fetchOptions={getClientOptions}
          placeholder="Client…"
          value={filters.clientId === "" ? "" : filters.clientId}
          onChange={(opt) => patch({ clientId: userSelectOnChange(opt) })}
        />
      </div>
    </div>
  );
}
